import { LoadingContainer } from 'peach/components'
import { useHistory, useParams } from 'peach/router'
import { useQueryClient } from '@tanstack/react-query'
import * as R from 'remeda'

import { DynamicForm, Page, PageHeader } from 'core/components'
import { useServiceCreate } from 'core/types'

const CreateMonitoringService = () => {
  const history = useHistory()
  const { companyId } = useParams<{ companyId?: string }>()

  const queryClient = useQueryClient()

  const { mutateAsync: createService } = useServiceCreate({
    companyId,
  })

  return (
    <LoadingContainer>
      <Page variant='full'>
        <PageHeader title='Create Monitoring Service' />
        <DynamicForm
          data={R.pipe(
            {
              name: '',
              serviceType: '',
              status: 'active',
              monitorIntervalType: 'daily',
              monitorIntervalValue: 1,
              gracePeriod: 0,
              positiveRateThreshold: undefined,
              ignoreThresholdBelowTotalBorrowers: undefined,
              hitMatchRequirements: [],
              femaDisasterTypesTracked: [],
            },
            // Convert any null values to empty strings for string fields to prevent React warnings
            R.mapValues((v) => (v === null ? '' : v)),
          )}
          fieldEditMode='all'
          fieldOverrides={{
            status: 'Select',
            monitorIntervalType: 'Select',
            serviceType: 'Select',
          }}
          omitFields={[
            'id',
            'companyId',
            'object',
            'createdAt',
            'updatedAt',
            'deletedAt',
          ]}
          onSubmit={async (payload: any) => {
            const processedPayload = R.pipe(
              payload,
              // Remove empty strings, null, undefined for optional fields
              R.omitBy((v) => {
                if (v === '' || v === null || v === undefined) return true
                if (
                  typeof v === 'number' &&
                  v === 0 &&
                  v !== payload.monitorIntervalValue
                )
                  return true
                return false
              }),
            )
            const res = await createService(processedPayload)
            if (res?.data?.id) {
              void queryClient.invalidateQueries({
                queryKey: ['companies', companyId, 'monitoring', 'services'],
              })
              history.push(
                `/companies/${companyId}/monitoring/services/${res.data.id}`,
              )
            } else {
              history.push(`/companies/${companyId}/monitoring/services`)
            }
          }}
          selectOptions={{
            status: [
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ],
            monitorIntervalType: [
              { label: 'Hourly', value: 'hourly' },
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' },
            ],
            serviceType: [
              { label: 'Bankruptcy', value: 'bankruptcy' },
              { label: 'Military Duty', value: 'militaryDuty' },
              { label: 'Deceased', value: 'deceased' },
              { label: 'Federal Emergency', value: 'federalEmergency' },
              {
                label: 'OFAC Sanctioned Individual',
                value: 'oFACSanctionedIndividual',
              },
              {
                label: 'Reassigned Numbers Database',
                value: 'reassignedNumbersDatabase',
              },
            ],
          }}
          submitText='Create Monitoring Service'
          validationRules={{
            name: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'Name is required',
            ],
            serviceType: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'Service type is required',
              (v: unknown) =>
                [
                  'bankruptcy',
                  'militaryDuty',
                  'deceased',
                  'federalEmergency',
                  'oFACSanctionedIndividual',
                  'reassignedNumbersDatabase',
                ].includes(v as string) ||
                'Service type must be one of the valid options',
            ],
          }}
        />
      </Page>
    </LoadingContainer>
  )
}

export default CreateMonitoringService
